import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { classes } from '../lib/divers';
import { Bouton } from './Boutons';

/**
 * Boîte de dialogue modale.
 *
 * Trois exigences non négociables sur un outil où les actions sont irréversibles (désassocier
 * un appareil, suspendre une exploitation) : Échap ferme, le focus entre dans la boîte à
 * l'ouverture et revient d'où il venait à la fermeture, et le fond ne défile pas derrière.
 */
export function Modale({
  titre,
  ouverte,
  surFermeture,
  children,
  pied,
  large = false,
}: {
  titre: string;
  ouverte: boolean;
  surFermeture: () => void;
  children: ReactNode;
  pied?: ReactNode;
  large?: boolean;
}): JSX.Element | null {
  const boite = useRef<HTMLDivElement>(null);
  const focusPrecedent = useRef<HTMLElement | null>(null);

  // La fermeture est lue dans une référence, jamais dans les dépendances de l'effet.
  // Les appelants passent une lambda en ligne : si elle entrait dans les dépendances, chaque
  // rendu du parent (un simple `isFetching` qui bascule) rejouerait l'effet, qui replacerait le
  // focus sur le premier élément de la boîte — l'opérateur en train de saisir un motif de
  // désassociation se ferait voler son curseur au milieu d'un mot.
  const fermer = useRef(surFermeture);
  fermer.current = surFermeture;

  useEffect(() => {
    if (!ouverte) return undefined;

    focusPrecedent.current = document.activeElement as HTMLElement | null;
    const debordement = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const auClavier = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        fermer.current();
      }
    };
    document.addEventListener('keydown', auClavier);

    // Premier élément focalisable de la boîte, à défaut la boîte elle-même.
    const cible = boite.current?.querySelector<HTMLElement>(
      'input, select, textarea, button, [href], [tabindex]:not([tabindex="-1"])',
    );
    (cible ?? boite.current)?.focus();

    return () => {
      document.removeEventListener('keydown', auClavier);
      document.body.style.overflow = debordement;
      focusPrecedent.current?.focus();
    };
  }, [ouverte]);

  if (!ouverte) return null;

  return createPortal(
    <div
      className="gv-modale-fond"
      role="presentation"
      onMouseDown={(e) => {
        // Fermeture au clic sur le fond seulement : un glisser-déposer parti de l'intérieur
        // ne doit pas fermer la boîte.
        if (e.target === e.currentTarget) surFermeture();
      }}
    >
      <div
        className={classes('gv-modale', large && 'gv-modale--large')}
        role="dialog"
        aria-modal="true"
        aria-label={titre}
        tabIndex={-1}
        ref={boite}
      >
        <header className="gv-modale__entete">
          <h2 className="gv-h3">{titre}</h2>
          <div className="gv-carte__entete-actions">
            <Bouton variante="discret" petit onClick={surFermeture} aria-label="Fermer">
              ✕
            </Bouton>
          </div>
        </header>
        <div className="gv-modale__corps">{children}</div>
        {pied ? <footer className="gv-modale__pied">{pied}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
